/**
 * Modal de seleção manual de material.
 * Lista todos os materiais disponíveis para o usuário escolher.
 * 
 * Segue padrões de UI do projeto:
 * - Usa componente Button do sistema
 * - Overlay padrão: bg-black/50 backdrop-blur-sm
 * - Container: bg-navy-800 border border-navy-700 rounded-lg
 */

import { useState } from 'react';
import { X, Check, Package, Search, Ruler } from 'lucide-react';
import { Button } from '../../../ui/Button';
import type { MaterialSelectModalProps, ProductRule } from '../types';

export function MaterialSelectModal({
  isOpen,
  rules,
  onSelect,
  onClose
}: MaterialSelectModalProps) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  if (!isOpen) return null;

  // Filtrar regras pela busca
  const filteredRules = rules.filter(rule => {
    const nome = rule.produto?.nome_servico?.toLowerCase() ?? '';
    const descricao = rule.produto?.descricao?.toLowerCase() ?? '';
    const diretorio = rule.diretorios?.nome?.toLowerCase() ?? '';
    const search = searchTerm.toLowerCase();
    return nome.includes(search) || descricao.includes(search) || diretorio.includes(search);
  });

  const handleConfirm = () => {
    const selectedRule = rules.find(r => r.produto?.id === selectedId);
    if (selectedRule) {
      onSelect(selectedRule);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-navy-800 border border-navy-700 rounded-lg w-full max-w-[400px] max-h-[70vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-navy-700 shrink-0">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-amber-500/20 rounded-lg">
              <Package className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <span className="text-sm font-semibold text-white">Selecionar Material</span>
              <p className="text-xs text-gray-400">{rules.length} disponíveis</p>
            </div>
          </div>
          <Button variant="ghost" size="xs" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Search */}
        <div className="p-3 border-b border-navy-700 shrink-0">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar material..."
              className="w-full pl-8 pr-3 py-2 text-sm bg-navy-900 border border-navy-600 rounded text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-2 min-h-0">
          {filteredRules.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
              {rules.length === 0 ? (
                <>
                  <p className="text-sm">Nenhum material habilitado para orçamento</p>
                  <p className="text-xs mt-1 text-gray-500">
                    Ative os materiais em "Exibir no orçamento" nas Regras de Produto.
                  </p>
                </>
              ) : (
                <p className="text-sm">Nenhum material encontrado</p>
              )}
            </div>
          ) : (
            <div className="space-y-1">
              {filteredRules.map((rule) => (
                <MaterialOption
                  key={rule.produto?.id ?? rule.regra?.id}
                  rule={rule}
                  isSelected={selectedId === rule.produto?.id}
                  onSelect={() => setSelectedId(rule.produto?.id ?? null)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 p-3 border-t border-navy-700 shrink-0">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={onClose}
            className="flex-1"
          >
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            size="sm"
            icon={Check}
            onClick={handleConfirm}
            disabled={selectedId === null}
            className="flex-1"
          >
            Confirmar
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Componente para cada opção de material na lista.
 */
interface MaterialOptionProps {
  rule: ProductRule;
  isSelected: boolean;
  onSelect: () => void;
}

function MaterialOption({ rule, isSelected, onSelect }: MaterialOptionProps) {
  const nome = rule.produto?.nome_servico ?? 'Material desconhecido';
  const diretorio = rule.diretorios?.nome;
  const dimensoes = rule.dimensoes;
  const hasDimensoes = dimensoes?.largura_cm != null && dimensoes?.altura_cm != null;

  return (
    <button
      onClick={onSelect}
      className={`w-full text-left p-2.5 rounded transition-all ${
        isSelected
          ? 'bg-blue-500/20 border border-blue-500/50 ring-1 ring-blue-500/30'
          : 'bg-navy-700/30 border border-transparent hover:bg-navy-700/50 hover:border-navy-600'
      }`}
    >
      <div className="flex items-start gap-2.5">
        {/* Radio button visual */}
        <div
          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all ${
            isSelected
              ? 'border-blue-500 bg-blue-500'
              : 'border-gray-500'
          }`}
        >
          {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium leading-tight ${isSelected ? 'text-blue-300' : 'text-white'}`}>
            {nome}
          </p>
          
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {diretorio && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-navy-600/50 rounded text-xs text-gray-400">
                <Package className="w-2.5 h-2.5" />
                {diretorio}
              </span>
            )}
            {hasDimensoes && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-navy-600/50 rounded text-xs text-gray-400">
                <Ruler className="w-2.5 h-2.5" />
                {dimensoes?.largura_cm} × {dimensoes?.altura_cm} cm
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}
